import React from "react";
import { createRoot } from "react-dom/client";
// Tipografia v2 (self-hosted via fontsource, sem CDN):
// Instrument Sans = UI inteira (font-sans) · Bricolage Grotesque = display (font-display).
import "@fontsource-variable/instrument-sans";
import "@fontsource-variable/bricolage-grotesque";
import App from "./App.tsx";
import { promptVersionUpdate, avisarDeployEmBreve } from "./lib/versionUpdate";
import "./index.css";
import "./styles/tv-effects.css";

// 🔄 Detector de versão antiga (chunk não encontrado pós-deploy).
// Quando o servidor é atualizado, os hashes dos arquivos JS mudam.
// Se o usuário tinha a aba aberta e navega pra uma rota lazy-loaded,
// o browser tenta baixar um arquivo que não existe mais e dá erro tipo
// "Failed to fetch dynamically imported module" ou "ChunkLoadError".
// Solução: capturar o erro e oferecer recarga em 1 clique.
window.addEventListener('prime:version-update', (event) => {
  promptVersionUpdate((event as CustomEvent<{ buildId: string }>).detail?.buildId);
});

// O deploy AVISA antes de reiniciar (deploy-aviso.json, servido pelo nginx).
// É o que permite a contagem aparecer ANTES da queda, em vez de a equipe
// descobrir o reinício clicando em algo que dá erro.
window.addEventListener('prime:deploy-em-breve', (event) => {
  const d = (event as CustomEvent<{ reinicioEm: number; expiraEm: number }>).detail;
  if (d?.reinicioEm && d?.expiraEm) avisarDeployEmBreve(d.reinicioEm, d.expiraEm);
});

// Vite emite este evento quando preload de chunk lazy falha.
window.addEventListener('vite:preloadError', () => promptVersionUpdate());

window.addEventListener('error', (event) => {
  if (/Loading chunk|Failed to fetch dynamically imported|ChunkLoadError|Importing a module script failed|error loading dynamically imported module/i.test(event.message || '')) {
    promptVersionUpdate();
  }
});

// Promise rejeitada por chunk não encontrado.
window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message || String(event.reason || '');
  if (/Failed to fetch dynamically imported module|Loading chunk \d+ failed|ChunkLoadError|importing the module|Importing a module script failed|error loading dynamically imported module/i.test(msg)) {
    promptVersionUpdate();
    event.preventDefault();
  }
});

// 🛡️ Proteção contra extensões que manipulam o DOM (Google Translate, etc.)
// Evita o erro "Failed to execute 'removeChild' on 'Node'"
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function<T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      console.warn('[DOM Protection] removeChild: node not a child, returning as-is');
      return child;
    }
    return originalRemoveChild.apply(this, [child]) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function<T extends Node>(newNode: T, refNode: Node | null): T {
    if (refNode && refNode.parentNode !== this) {
      console.warn('[DOM Protection] insertBefore: ref node not a child, appending');
      return originalInsertBefore.apply(this, [newNode, null]) as T;
    }
    return originalInsertBefore.apply(this, [newNode, refNode]) as T;
  };
}

createRoot(document.getElementById("root")!).render(<App />);
